<template>
  <div>
    <div
      class="flex flex-col"
      :class="{'mb-2': this.isTemplateContentSubmitted && !this.templateMetaData.templateFolder,
          'mb-6': !(this.isTemplateContentSubmitted && !this.templateMetaData.templateFolder)}"
    >
      <label class="font-semibold text-sm text-grey-700 mb-2">
        {{ $t('frontend_groovemail_adminsetting_category') }}
      </label>
      <treeselect
        class="custom-treeselect treeselect-add-new "
        v-model="templateMetaData.templateFolder"
        :max-height="200"
        :multiple="false"
        :options="folderList"
        :clearOnSelect="true"
        :closeOnSelect="true"
        :clearable="false"
        @select="updatedFolder"
        :placeholder="$t('frontend_groovemail_admin_global_templates_select_category_text')"
      />
      <div
        class="text-sm ttext-danger mt-2"
        v-if="this.isTemplateContentSubmitted && !this.templateMetaData.templateFolder"
      >
        {{ $t('frontend_groovemail_admin_global_templates_please_select_category') }}
      </div>
    </div>
    <div class="flex flex-col mb-6">
      <label class="font-semibold text-sm text-grey-700 mb-2">
        {{ 
          (from === 'global' || from === 'my-template' || saveAs === 2) 
            ? $t('frontend_groovemail_modal_email_template_name_of_template')
            : $t('frontend_groovemail_email_builder_past_template_email_name')
        }}
      </label>
      <text-input
        class="inline-block mr-2"
        variant="base"
        v-model="templateMetaData.templateName"
        :validator="$v.templateMetaData.templateName"
        :placeholder="
          (from === 'global' || from === 'my-template' || saveAs === 2)
          ? $t('frontend_groovemail_modal_email_template_enter_name_of_template')
          : $t('frontend_groovemail_email_builder_template_name_placeholder')
        "
      />
    </div>
    <div class="flex flex-col" v-if="from === 'global'">
      <label class="font-semibold text-sm text-grey-700 mb-2">
        {{ $t('frontend_groovemail_admin_global_templates_search_keywords_header') }}
      </label>
      <div class="flex">
        <text-input
          class="inline-block mr-2"
          variant="base"
          v-model="searchKeyword"
          :validator="$v.searchKeyword"
          :placeholder="$t('frontend_groovemail_admin_global_templates_enter_keywords_header')"
        >
          <SearchIcon slot="prependIcon" />
        </text-input>
        <gd-button
          variant="primary"
          size="icon"
          class="h-10 w-10"
          :disabled="this.$v.searchKeyword.$invalid"
          @click="addSearchKeywords"
        >
          <PlusIcon class="h-7 w-7" />
        </gd-button>
      </div>
      <div class="flex mt-6">
        <div class="w-full text-left">
          <div class="flex flex-wrap">
            <div
              v-for="(keyword, keywordIndex) in templateMetaData.templateSearchKeyword"
              :key="keywordIndex"
              class="flex flex-row rounded px-2 py-1 mr-2 mb-2 font-semibold text-xs bg-primary text-primary-invert"
            >
              <div class="font-semibold pr-2">
                {{ keyword }}
              </div>
              <div
                class="cursor-pointer hover:text-danger"
                @click="deleteKeyword(keywordIndex)"
              >
                <i class="fas fa-times"></i>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="flex justify-end mt-6">
      <gd-button
        size="auto"
        @click="closeTemplateMetaModal"
        >{{ $t('frontend_cancel') }}</gd-button
      >
      <gd-button
        variant="primary"
        size="auto"
        class="ml-2"
        @click="saveTemplateMetaData"
      >
        {{ $t('frontend_save') }}</gd-button
      >
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { required, maxLength, minLength, alphaNum } from 'vuelidate/lib/validators';
import Treeselect from '@riophae/vue-treeselect';
import '@riophae/vue-treeselect/dist/vue-treeselect.css';

// import icons
import PlusIcon from '@/assets/images/icons/new/plus-small_new.svg';
import SearchIcon from '@/assets/images/icons/new/search_new.svg';

export default {
  middleware: 'auth',
  name: 'template-meta-data',
  props: {
    templateMeta: {
      required: true,
    },
    from: {
      required: true,
    },
    saveAs: {
      required: true,
    },
  },

  validations: {
    templateMetaData: {
      templateName: {
        required,
        minLength: minLength(3),
        maxLength: maxLength(50),
      },
    },
    searchKeyword: {
      required,
      alphaNum,
      minLength: minLength(3),
      maxLength: maxLength(20),
    },
  },

  data() {
    return {
      isTemplateContentSubmitted: false,
      templateMetaData: this.templateMeta,
      searchKeyword: '',
      folders: [],
    };
  },

  components: {
    Treeselect,
    PlusIcon,
    SearchIcon,
  },

  watch: {
    'templateMetaData': function watchTemplateData() {
      this.$emit('updatedTemplateMeta', this.templateMetaData);
    }
  },

  computed: {
    folderList() {
      const folderList = [];
      if (this.folders && this.folders.length) {
        this.folders.forEach((element) => {
          folderList.push({ id: element.id, label: element.name });
        });
      }
      const folderIndex = folderList.findIndex((folderEle) => {
        return folderEle.id === 'createFolderOption1';
      });
      if (folderIndex < 0) {
        folderList.push({
          id: 'createFolderOption1',
          label: this.$t(
            'frontend_new_category'
          ),
        });
      }
      return folderList;
    },
  },

  async created() {
    await this.fetchFolders();
  },

  methods: {

    resetTemplateFolder() {
      this.templateMetaData.templateFolder = null;
    },

    updatedFolder(node) {
      if (node.id === 'createFolderOption1') {
        this.$emit('openCreateFolderModal');
        this.resetTemplateFolder();
      }
    },

    addSearchKeywords() {
      this.$v.searchKeyword.$touch();
      if (!this.$v.searchKeyword.$invalid) {
        this.templateMetaData.templateSearchKeyword.push(this.searchKeyword);
      }
      this.$v.searchKeyword.$reset();
      this.searchKeyword = '';
    },

    deleteKeyword(index) {
      this.templateMetaData.templateSearchKeyword.splice(index, 1);
    },

    saveTemplateMetaData () {
      this.isTemplateContentSubmitted = true;
      this.$v.templateMetaData.templateName.$touch();
      if (this.from === "global") {
        if (
          !this.$v.templateMetaData.templateName.$invalid &&
          this.templateMetaData.templateFolder) {
          this.$emit('saveTemplateWithMeta');
        }
      } else if (
        !this.$v.templateMetaData.templateName.$invalid &&
        this.templateMetaData.templateFolder
      ) {
        this.$emit('saveTemplateWithMeta');
      }
    },

    closeTemplateMetaModal () {
      this.$emit('closeTemplateMetaModal');
    },

    /**
     * Validates the folder data and sets the folders array if the data is valid.
     * @param {string} path - The path to fetch the folder data from.
    */
    async folderDataValidation(path) {
      try {
        // Show the loader while the request is being made.
        this.$emit('showLoader');

        // Send a GET request to fetch the folder data.
        const folderData = await axios.get(path);

        // If the folder data is valid, set the folders array.
        if (
          folderData &&
          folderData.data &&
          folderData.data.data &&
          folderData.data.data.length
        ) {
          this.folders = folderData.data.data;
        }

        // Hide the loader after the request is complete.
        this.$emit('hideLoader');
      } catch (e) {
        // If the request fails, hide the loader.
        this.$emit('hideLoader');
      }
    },

    /**
     * Fetches the folder data for the templates.
     * @param {string} id - The ID of the template folder to fetch.
    */
    fetchFolders(id) {
      // If an ID is provided, set the template folder to the specified ID.
      if (id) {
        this.templateMetaData.templateFolder = id;
      }

      // If the folders are being fetched for the global templates, fetch the folder data from the admin template subcategory path.
      if (this.from === 'global') {
        this.folderDataValidation('/admin/template-subcategory');
      } else {
        // If the folders are being fetched for the user templates, fetch the folder data from the user template folders path.
        this.folderDataValidation('/user-template-folders');
      }
    }
  },
};
</script>
